import React from 'react'
import {
  PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, ComposedChart, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ScatterChart, Scatter, ZAxis
} from 'recharts'

const COLORS = ['#0ea5e9', '#f59e0b', '#10b981', '#f43f5e', '#8b5cf6', '#64748b']

export function ChartRenderer({ code }: { code: string }) {
  try {
    const config = JSON.parse(code)
    const { type, data, options } = config

    if (type === 'pie') {
      return (
        <div style={{ width: '100%', height: 300, margin: '1rem 0' }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )
    }

    if (type === 'bar') {
      return (
        <div style={{ width: '100%', height: 350, margin: '1rem 0' }}>
          <ResponsiveContainer>
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {options?.bars?.map((bar: string, index: number) => (
                <Bar key={bar} dataKey={bar} fill={COLORS[index % COLORS.length]}>
                  {options.bars.length === 1 && data.map((entry: any, dataIndex: number) => (
                    <Cell key={`cell-${dataIndex}`} fill={COLORS[dataIndex % COLORS.length]} />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )
    }

    if (type === 'line') {
      return (
        <div style={{ width: '100%', height: 350, margin: '1rem 0' }}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {options?.lines?.map((line: string, index: number) => (
                <Line key={line} type="monotone" dataKey={line} stroke={COLORS[index % COLORS.length]} strokeWidth={3} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )
    }

    if (type === 'composed') {
      return (
        <div style={{ width: '100%', height: 350, margin: '1rem 0' }}>
          <ResponsiveContainer>
            <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" orientation="left" stroke={COLORS[0]} />
              {options?.hasRightYAxis && (
                <YAxis yAxisId="right" orientation="right" stroke={COLORS[1]} />
              )}
              <Tooltip />
              <Legend />
              {options?.bars?.map((bar: string, index: number) => (
                <Bar key={bar} yAxisId="left" dataKey={bar} fill={COLORS[0]} barSize={40} />
              ))}
              {options?.lines?.map((line: string, index: number) => (
                <Line key={line} yAxisId={options?.hasRightYAxis ? 'right' : 'left'} type="monotone" dataKey={line} stroke={COLORS[1]} strokeWidth={3} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )
    }

    if (type === 'radar') {
      return (
        <div style={{ width: '100%', height: 350, margin: '1rem 0' }}>
          <ResponsiveContainer>
            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
              <PolarGrid />
              <PolarAngleAxis dataKey="name" />
              <PolarRadiusAxis angle={30} domain={[0, 'auto']} />
              <Tooltip />
              <Legend />
              {options?.radars?.map((radar: string, index: number) => (
                <Radar key={radar} name={radar} dataKey={radar} stroke={COLORS[index % COLORS.length]} fill={COLORS[index % COLORS.length]} fillOpacity={0.6} />
              ))}
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )
    }

    if (type === 'scatter') {
      return (
        <div style={{ width: '100%', height: 350, margin: '1rem 0' }}>
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="x" type="number" name={options?.xName || 'X'} unit={options?.xUnit || ''} />
              <YAxis dataKey="y" type="number" name={options?.yName || 'Y'} unit={options?.yUnit || ''} />
              {options?.hasZAxis && (
                <ZAxis dataKey="z" type="number" range={options?.zRange || [50, 400]} name={options?.zName || 'Z'} unit={options?.zUnit || ''} />
              )}
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Legend />
              {options?.scatters?.map((scatter: { name: string, dataKey: string }, index: number) => (
                <Scatter key={scatter.name} name={scatter.name} data={data[scatter.dataKey]} fill={COLORS[index % COLORS.length]} />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )
    }

    if (type === 'heatmap') {
      const { rows, columns, matrix, colorScheme = 'blue' } = options;
      // Define a generic color gradient based on percentage
      const getColor = (value: number, min: number, max: number) => {
        const p = max === min ? 0.5 : (value - min) / (max - min);
        // blue scale
        if (colorScheme === 'blue') {
          return `rgba(14, 165, 233, ${Math.max(0.1, p)})`;
        }
        // red scale
        return `rgba(244, 63, 94, ${Math.max(0.1, p)})`;
      }
      
      let min = Infinity;
      let max = -Infinity;
      matrix.forEach((row: number[]) => row.forEach(val => {
        if (val < min) min = val;
        if (val > max) max = val;
      }));

      return (
        <div style={{ width: '100%', overflowX: 'auto', margin: '1rem 0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: `auto repeat(${columns.length}, 1fr)`, gap: '2px', minWidth: '600px' }}>
            {/* Header row */}
            <div style={{ padding: '0.5rem', fontWeight: 'bold' }}></div>
            {columns.map((col: string, c: number) => (
              <div key={c} style={{ padding: '0.5rem', fontWeight: 'bold', textAlign: 'center', backgroundColor: '#f1f5f9' }}>
                {col}
              </div>
            ))}
            
            {/* Data rows */}
            {rows.map((rowLabel: string, r: number) => (
              <React.Fragment key={r}>
                <div style={{ padding: '0.5rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', backgroundColor: '#f1f5f9' }}>
                  {rowLabel}
                </div>
                {matrix[r].map((val: number, c: number) => (
                  <div key={`${r}-${c}`} style={{ 
                    padding: '0.75rem 0.5rem', 
                    textAlign: 'center', 
                    backgroundColor: getColor(val, min, max),
                    color: (val - min) / (max - min) > 0.6 ? 'white' : 'black',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {val}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )
    }

    return <div>Unsupported chart type: {type}</div>
  } catch (e) {
    return <div style={{ color: 'red', border: '1px solid red', padding: '1rem' }}>Lỗi render biểu đồ: Vui lòng kiểm tra lại cấu trúc dữ liệu JSON.</div>
  }
}
